import _ from 'underscore';

import { File } from './file';
import Base, { baseInit } from './base';
import {
  CT_NORMAL,
  CT_PAYPERCALL,
  CT_SEARCH,
  CT_SOCIAL,
  CT_URL_ONLY,
  CT_YOUTUBE_BUMPER_VIDEO,
  CT_YOUTUBE_NONSKIPPABLE_VIDEO,
  CT_NATIVE_VIDEO,
  CT_NATIVE_DISPLAY,
  CT_NATIVE_ARTICLE,
  FILE_TYPE_UPLOAD,
  FILE_TYPE_LINK,
  CLOSED_CAPTIONING_UPLOAD,
  FILE_TYPE_CREATIVE,
  CT_SOCIAL_TIKTOK,
  CT_SOCIAL_SNAPCHAT,
} from '../constant';
import { isBlank } from '../helpers/common';
import { Headline } from './headline';
import { CompanionAsset } from './companion-asset';

export class Creative extends Base {
  _uuid = null; // String - internal field used for referencing creatives
  _updatedCreative = false; // Boolean - internal flag for dirty check

  id = null; // Integer
  creativeType = null; // String/enum CT_*
  addCloseCaptioning = false; // Boolean Checkbox
  companionAsset = false; // Boolean Checkbox
  clickThroughURL = null;
  creativeStartDate = null; // 2019-04-09T18:46:21.640Z;
  creativeEndDate = null; // 2019-04-09T18:46:21.640Z;
  files = null; // [ File{} ]
  headlines = []; // [ Headline{} ]
  companionAssets = [];
  playTypeId = null;
  bodyText = null;
  description1 = null;
  description2 = null;
  displayPath1 = null;
  displayPath2 = null;
  headline1 = null;
  headline2 = null;
  headline3 = null;
  socialUrl = null;

  businessName = null;
  phoneNumber = null;
  callToAction = null;
  previewText = null;
  trueView = false;
  cta = null;
  name = null;
  brandName = null;

  constructor(data) {
    super(data);
    baseInit(this, data, true);
    if (this.files === null) {
      this.files = [];
    } else {
      this.files = this.files.map((f) => new File(f));
    }
    if (this.headlines === null) {
      this.headlines = [];
    } else {
      this.headlines = this.headlines.map((f) => new Headline(f));
    }
    if (this.companionAssets === null) {
      this.companionAssets = [];
    } else {
      this.companionAssets = this.companionAssets.map((f) => new CompanionAsset(f));
    }
  }

  newUUID(resetIDs) {
    super.newUUID(resetIDs);
    if (resetIDs) {
      // recurse to nested objects
      if (!isBlank(this.files)) {
        this.files.forEach((file) => {
          file.newUUID(resetIDs);
        });
      }
      if (!isBlank(this.headlines)) {
        this.headlines.forEach((h) => {
          h.newUUID(resetIDs);
        });
      }
      if (!isBlank(this.companionAssets)) {
        this.companionAssets.forEach((c) => {
          c.newUUID(resetIDs);
        });
      }
    }
  }

  // Valid if all fields are set for the given creative type
  isValid() {
    if (this.creativeType === CT_NORMAL) {
      return (
        !_.isEmpty(this.clickThroughURL) && !_.isEmpty(this.files) && this.allFilesHaveValidSize()
      );
    }
    if (this.creativeType === CT_NATIVE_VIDEO) {
      const validFileTypes = [FILE_TYPE_CREATIVE, FILE_TYPE_LINK, FILE_TYPE_UPLOAD];
      const creativeFiles = this.files
        ? this.files.filter((f) => validFileTypes.includes(f.type))
        : null;
      const captioningFiles = this.files
        ? this.files.filter((f) => f.type === CLOSED_CAPTIONING_UPLOAD)
        : null;

      return (
        !_.isEmpty(this.clickThroughURL) &&
        !_.isEmpty(creativeFiles) &&
        (!this.addCloseCaptioning || (this.addCloseCaptioning && !_.isEmpty(captioningFiles)))
      );
    }

    if (this.creativeType === CT_NATIVE_ARTICLE) {
      const validFileTypes = [FILE_TYPE_CREATIVE, FILE_TYPE_LINK, FILE_TYPE_UPLOAD];
      const creativeFiles = this.files
        ? this.files.filter((f) => validFileTypes.includes(f.type))
        : null;

      return !_.isEmpty(this.clickThroughURL) && !_.isEmpty(creativeFiles);
    }

    if (this.creativeType === CT_NATIVE_DISPLAY) {
      const validFileTypes = [FILE_TYPE_CREATIVE, FILE_TYPE_LINK, FILE_TYPE_UPLOAD];
      const creativeFiles = this.files
        ? this.files.filter((f) => validFileTypes.includes(f.type))
        : null;

      return !_.isEmpty(this.clickThroughURL) && !_.isEmpty(creativeFiles);
    }

    if (this.creativeType === CT_SEARCH) {
      return (
        !_.isEmpty(this.clickThroughURL) &&
        !_.isEmpty(this.headline1) &&
        !_.isEmpty(this.headline2) &&
        !_.isEmpty(this.headline3) &&
        !_.isEmpty(this.displayPath1) &&
        !_.isEmpty(this.displayPath2) &&
        !_.isEmpty(this.description1) &&
        !_.isEmpty(this.description2)
      );
    }
    if (this.creativeType === CT_SOCIAL) {
      return (
        !_.isEmpty(this.clickThroughURL) && !_.isEmpty(this.socialUrl) && !_.isEmpty(this.files)
      );
    }
    if (this.creativeType === CT_SOCIAL_TIKTOK) {
      return (
        !_.isEmpty(this.clickThroughURL) &&
        !_.isEmpty(this.brandName) &&
        !_.isEmpty(this.files) &&
        (!this.bodyText || this.bodyText.length < 100)
      );
    }
    if (this.creativeType === CT_SOCIAL_SNAPCHAT) {
      return (
        !_.isEmpty(this.clickThroughURL) &&
        !_.isEmpty(this.brandName) &&
        !_.isEmpty(this.headline1) &&
        !_.isEmpty(this.files) &&
        this.brandName.length <= 25 &&
        this.headline1.length <= 34
      );
    }
    if (this.creativeType === CT_PAYPERCALL) {
      return (
        !_.isEmpty(this.clickThroughURL) &&
        !_.isEmpty(this.businessName) &&
        !_.isEmpty(this.phoneNumber) &&
        !_.isEmpty(this.headline1) &&
        !_.isEmpty(this.description1)
      );
    }
    if (this.creativeType === CT_URL_ONLY) {
      return !_.isEmpty(this.clickThroughURL) && !_.isEmpty(this.files);
    }

    if (this.creativeType === CT_YOUTUBE_BUMPER_VIDEO) {
      return !_.isEmpty(this.clickThroughURL) && !_.isEmpty(this.files);
    }

    if (this.creativeType === CT_YOUTUBE_NONSKIPPABLE_VIDEO) {
      if (this.trueView) {
        return (
          !_.isEmpty(this.clickThroughURL) &&
          !_.isEmpty(this.files) &&
          !_.isEmpty(this.cta) &&
          !_.isEmpty(this.headline1)
        );
      }
      return !_.isEmpty(this.clickThroughURL) && !_.isEmpty(this.files);
    }
    return true;
  }

  // Checks if all creative fields are blank, e.g., (new Creative()).isEmpty() === true
  isEmpty() {
    return Object.keys(this).every((key) => {
      // return true if internal field OR blank
      return (
        key.includes('_') ||
        key === 'creativeType' ||
        key === 'addCloseCaptioning' ||
        key === 'companionAsset' ||
        key === 'trueView' ||
        key === 'creativeStartDate' ||
        key === 'creativeEndDate' ||
        isBlank(this[key])
      );
    });
  }

  isDirty() {
    return this._updatedCreative === true;
  }

  allFilesHaveValidSize() {
    return this.files.every((file) => {
      return file instanceof File ? file.isValid() : new File(file).isValid();
    });
  }
}
